import debounce from 'lodash/fp/debounce.js';
import get from 'lodash/fp/get.js';
import { createSelector } from 'reselect';
import { getFormInitialValues, getFormValues, updateSyncErrors } from 'redux-form';
import makeFormValidator from '@hbrisk/sp3-risk-model-support/utility/form/validation/makeFormValidator/index.js';
import findFieldDefinition from '@hbrisk/sp3-risk-model-support/utility/form/findFieldDefinition/index.js';
import makeSelectAutofilled from '#selectors/ui/form/support/makeSelectAutofilled.js';
import makeSelectVisibleFields from '#selectors/ui/form/support/makeSelectVisibleFields.js';
import {
  AUTOFILL_FORM_FIELD,
  HIDE_FIELD,
  INITIALIZE_FORM,
  REDUX_FORM_ARRAY_INSERT,
  REDUX_FORM_ARRAY_MOVE,
  REDUX_FORM_ARRAY_POP,
  REDUX_FORM_ARRAY_PUSH,
  REDUX_FORM_ARRAY_REMOVE,
  REDUX_FORM_ARRAY_SPLICE,
  REDUX_FORM_ARRAY_SWAP,
  REDUX_FORM_ARRAY_REMOVE_ALL,
  REDUX_FORM_ARRAY_SHIFT,
  REDUX_FORM_ARRAY_UNSHIFT,
  REDUX_FORM_CHANGE,
  SHOW_FIELD,
  UNAUTOFILL_FORM_FIELD,
} from '#constants/actionTypes.js';

const saveTypes = [
  AUTOFILL_FORM_FIELD,
  HIDE_FIELD,
  REDUX_FORM_ARRAY_POP,
  REDUX_FORM_ARRAY_PUSH,
  REDUX_FORM_ARRAY_REMOVE,
  REDUX_FORM_ARRAY_REMOVE_ALL,
  REDUX_FORM_ARRAY_SHIFT,
  REDUX_FORM_ARRAY_UNSHIFT,
  REDUX_FORM_CHANGE,
  SHOW_FIELD,
  UNAUTOFILL_FORM_FIELD,
];

const validationTypes = [...saveTypes, INITIALIZE_FORM];

const partiallySupportedFieldArrayActions = [
  REDUX_FORM_ARRAY_INSERT,
  REDUX_FORM_ARRAY_MOVE,
  REDUX_FORM_ARRAY_SPLICE,
  REDUX_FORM_ARRAY_SWAP,
];

const makeFormMiddleware = ({
  form,
  fields,
  saveActionCreator,
  selectCurrentUuid,
  calculatePayload,
  saveDebounceInterval,
  validateDebounceInterval,
  mapStateToParams,
  extensions,
}) => {
  const save = store => {
    const { dispatch, getState } = store;
    const state = getState();
    const uuid = selectCurrentUuid(state);
    const values = getFormValues(form)(state);
    const autofilled = makeSelectAutofilled(form)(state);
    const visibleFields = makeSelectVisibleFields(form)(state);
    const rest = typeof mapStateToParams === 'function' ? mapStateToParams(state) : {};
    const payload = calculatePayload({
      values,
      autofilled,
      visibleFields,
      form,
      fields,
      ...rest,
    });
    dispatch(saveActionCreator({ uuid, payload, ...rest }));
  };
  const saveWithDebounce = debounce(saveDebounceInterval || 2000, save);

  const validate = store => {
    const { dispatch, getState } = store;
    const state = getState();
    const values = getFormValues(form)(state);
    const autofilled = makeSelectAutofilled(form)(state);
    const visibleFields = makeSelectVisibleFields(form)(state);
    const syncErrors = makeFormValidator(fields)(autofilled, visibleFields, state, values);
    dispatch(updateSyncErrors(form, syncErrors));
  };

  const validateWithDebounce = debounce(validateDebounceInterval || 500, validate);

  const selectFormInitialized = createSelector(
    state => getFormInitialValues(form)(state),
    initial => initial !== undefined
  );
  return store => next => action => {
    const { getState } = store;
    const prevState = getState();
    const result = next(action);
    const { type } = action;

    const actionForm = get('meta.form', action);
    const state = getState();
    const initialized = selectFormInitialized(state);
    if (form === actionForm && saveTypes.includes(type) && initialized) {
      saveWithDebounce(store);
    }
    if (form === actionForm && validationTypes.includes(type) && initialized) {
      validateWithDebounce(store);
    }
    if (form === actionForm && partiallySupportedFieldArrayActions.includes(type)) {
      const actionfield = get('meta.field', action);
      const { supportsNestedAutofilling, supportsNestedVisibility } = findFieldDefinition(
        actionfield,
        fields
      );
      if ([supportsNestedAutofilling, supportsNestedVisibility].includes(true)) {
        throw new Error(
          `Action type ${type} is not currently supported for field arrays with nested visibility and/or autofilled statuses. Follow stack trace for possible resolutions.`
        );
        // To resolve this error either (i) set both "supportsNestedAutofilling"" and
        // "supportsNestedVisibility" to false in the relevant field definition or (ii) (a) update
        // makeFormReducerPlugin to support nested visibility and autofilled statuses with this
        // action and (b) remote the action type from the partiallySupportedFieldArrayActions array
        // above.
      }
    }
    if (Array.isArray(extensions) && initialized) {
      extensions.forEach(extension => {
        extension({
          store,
          action,
          form,
          validate,
          prevState,
        });
      });
    }
    return result;
  };
};

export default makeFormMiddleware;
